import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

import { createWarehouseLayout } from 'repositories/HttpRepository';
import { warehouseSetupErrorMessage } from 'utils/warehouseLocationSetup';

export default function useLocationLayoutForm({
  warehouseId,
  region,
  defaultLayoutName,
  initialLocations,
  forceUpdate = false,
  onSuccess,
}) {
  const { control, handleSubmit, getValues } = useForm({
    defaultValues: {
      name: defaultLayoutName || '',
      locations: initialLocations?.length ? initialLocations : [],
    },
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'locations', keyName: 'key' });
  const [submitError, setSubmitError] = useState('');
  const [layoutExists, setLayoutExists] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const save = (values, overwrite) => {
    const payload = {
      name: values.name.trim(),
      region: (region ?? '').trim(),
      locations: (values.locations ?? []).map((location) => {
        const capacity = location.containerCapacity === '' ? NaN : Number(location.containerCapacity);
        return {
          id: (location.id ?? '').trim(),
          name: (location.name ?? '').trim(),
          containerCapacity: Number.isNaN(capacity) ? -1 : capacity,
        };
      }),
    };
    setSubmitting(true);
    setSubmitError('');
    return createWarehouseLayout(warehouseId, payload, overwrite)
      .then((res) => {
        if (res.ok) {
          onSuccess(payload);
          return;
        }
        if (res.data?.id === 'warehouse-location-layout-exists') setLayoutExists(true);
        setSubmitError(warehouseSetupErrorMessage(res.data?.id) || 'Failed to create the location layout.');
      })
      .catch(() => {
        setSubmitError('Failed to create the location layout.');
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const isUniqueLocationId = (value) => {
    const ids = getValues('locations').map((location) => (location.id || '').trim());
    return ids.filter((id) => id === (value || '').trim()).length <= 1 || 'Location ids must be unique';
  };

  const addLocation = () => append({ id: '', name: '', containerCapacity: -1, isNew: true });
  const submit = handleSubmit((values) => save(values, forceUpdate));
  const submitOverwrite = handleSubmit((values) => save(values, true));

  return {
    control,
    fields,
    addLocation,
    removeLocation: remove,
    isUniqueLocationId,
    submit,
    submitOverwrite,
    submitError,
    layoutExists,
    submitting,
  };
}
